import { useTour } from "@bcc/contexts/tour-context";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { buildSetupGuideFinishCopyKeys, TourFinishDialog, WHATS_NEW_FINISH_COPY_KEYS } from "../tour-finish-dialog/tour-finish-dialog";
import { TourLoadingCard } from "../tour-loading-card/tour-loading-card";
import { TourSpotlight } from "../tour-spotlight/tour-spotlight";
import { TourStepCard } from "../tour-step-card/tour-step-card";
import { buildSetupGuideWelcomeCopyKeys, TourWelcomeDialog, WHATS_NEW_WELCOME_COPY_KEYS } from "../tour-welcome-dialog/tour-welcome-dialog";
import { useTourTarget } from "../use-tour-target";
import type { SetupGuidePage } from "../tour.types";
import { useTourTextParams } from "../use-tour-text-params";

/** The features behind the steps, in tour order and without repeats, named like the step card badge */
const getAnnouncedFeatureIds = (stepIds: readonly string[]): string[] => [...new Set(stepIds.map((id) => id.split(".")[0] ?? id))];

/** Setup-guide copy per page, the what's-new copy, or undefined for the full tour's default copy */
const resolveCopyKeys = (setupGuidePage: SetupGuidePage | undefined, isWhatsNew: boolean) => {
  if (setupGuidePage) {
    return {
      welcomeCopyKeys: buildSetupGuideWelcomeCopyKeys(setupGuidePage),
      finishCopyKeys: buildSetupGuideFinishCopyKeys(setupGuidePage),
    };
  }
  if (isWhatsNew) return { welcomeCopyKeys: WHATS_NEW_WELCOME_COPY_KEYS, finishCopyKeys: WHATS_NEW_FINISH_COPY_KEYS };
  return { welcomeCopyKeys: undefined, finishCopyKeys: undefined };
};

/**
 * Renders whichever tour surface matches the current status: the welcome
 * dialog, the spotlight + step card while running, or the finish dialog.
 * Mount once inside the TourProvider (next to the app routes).
 */
export function TourRenderer() {
  const { t } = useTranslation();
  const { state, next, prev, skip, finish, beginSteps, skipUnavailableStep } = useTour();
  const isRunning = state.status === "running";
  const currentStep = isRunning ? state.steps[state.currentIndex] : undefined;
  const { rect } = useTourTarget({ step: currentStep, active: isRunning, onTargetMissing: skipUnavailableStep, onAdvance: next });
  const textParams = useTourTextParams();
  const isTargetEmpty = Boolean(rect && currentStep?.emptyState && !document.querySelector(currentStep.emptyState.detectTarget));
  const setupGuidePage = state.mode === "onboarding" ? state.setupGuidePage : undefined;
  const isWhatsNew = state.mode === "whatsNew";
  const { welcomeCopyKeys, finishCopyKeys } = resolveCopyKeys(setupGuidePage, isWhatsNew);
  const featureTitles = isWhatsNew
    ? getAnnouncedFeatureIds(state.steps.map((step) => step.id)).map((id) => t(`tour.features.${id}.title`) as string)
    : undefined;
  // An interactive step without its target is waiting for the user, not for the page
  const waitingMessageKey = currentStep?.interactive ? (currentStep.hintKey ?? "tour.controls.waitingForYou") : undefined;

  let tourSurface: ReactNode = null;
  if (isRunning && currentStep) {
    tourSurface = rect ? (
      <>
        <TourSpotlight rect={rect} padding={currentStep.spotlightPadding} interactive={currentStep.interactive} />
        <TourStepCard
          step={currentStep}
          stepIndex={state.currentIndex}
          totalSteps={state.steps.length}
          targetRect={rect}
          isTargetEmpty={isTargetEmpty}
          textParams={textParams}
          onNext={next}
          onPrev={prev}
          onSkip={skip}
        />
      </>
    ) : (
      // Keeps the tour visible while a slow page loads and its target is polled
      <TourLoadingCard interactive={currentStep.interactive} onSkip={skip} messageKey={waitingMessageKey} />
    );
  }

  return (
    <>
      <TourWelcomeDialog
        isOpen={state.status === "welcome"}
        stepCount={state.steps.length}
        onBegin={beginSteps}
        onSkip={skip}
        copyKeys={welcomeCopyKeys}
        featureTitles={featureTitles}
      />
      {tourSurface && createPortal(tourSurface, document.body)}
      <TourFinishDialog isOpen={state.status === "finished"} wasSkipped={state.wasSkipped} onDone={finish} copyKeys={finishCopyKeys} />
    </>
  );
}
